// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorIcon,
  FormField,
  GhostButton,
  Input,
  SearchInput,
  SecondaryButton,
  Select,
  XIcon,
} from "../../design-system";
import { ClockIcon, EyeIcon } from "../../design-system/icons";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import { formatCount, formatDateTime, formatEmailVariant, formatRelativeTime } from "../components/automations/automationUtils";
import ChannelChip, { TemplateChip } from "../components/messages/ChannelChip";
import EmailSuppressionCard from "../components/messages/EmailSuppressionCard";
import MessageDetailModal from "../components/messages/MessageDetailModal";
import MessageStatusBadge from "../components/messages/MessageStatusBadge";
import OriginLabel from "../components/messages/OriginLabel";
import RecipientKindBadge, { PaidChip } from "../components/messages/RecipientKindBadge";
import {
  BUCKETS,
  CHANNELS,
  ORDERING_OPTIONS,
  ORIGINS,
  PAGE_SIZE_OPTIONS,
  RECIPIENT_KINDS,
  bucketCounts,
  clearedFilters,
  countActiveFilters,
  defaultOrderingLabel,
  describeRecipient,
  filtersForBucket,
  filtersToApiParams,
  filtersToSearchParams,
  formatOriginDetail,
  getBucketMeta,
  getChannelMeta,
  getMessageStatusMeta,
  getOriginMeta,
  getRecipientKindMeta,
  historyTimestamp,
  isCancellable,
  normalizeFilters,
  parseFiltersFromSearchParams,
} from "../components/messages/messagesUtils";
import { ADMIN_MESSAGES_QUERY_KEY, useAdminMessages, useCancelAdminMessage } from "../hooks/useAdminMessages";
import { useAdminMessagesFiltersMeta } from "../hooks/useAdminMessagesFiltersMeta";
import { Toast, useToast } from "../utils/adminToast";

const SEARCH_DEBOUNCE_MS = 350;

// ---------------------------------------------------------------------------
// Select options
// ---------------------------------------------------------------------------

const KIND_OPTIONS = [
  { value: "", label: "All" },
  ...RECIPIENT_KINDS.map((kind) => ({ value: kind, label: `${getRecipientKindMeta(kind).label}s` })),
];

const PAID_OPTIONS = [
  { value: "", label: "Any" },
  { value: "true", label: "Paid" },
  { value: "false", label: "Free" },
];

const ORIGIN_OPTIONS = [
  { value: "", label: "All" },
  ...ORIGINS.map((origin) => ({ value: origin, label: `${getOriginMeta(origin).label}s` })),
];

const CHANNEL_OPTIONS = [{ value: "", label: "All" }, ...CHANNELS.map((c) => ({ value: c, label: getChannelMeta(c).label }))];

const PAGE_SIZE_SELECT_OPTIONS = PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: `${n} per page` }));

/** Options list that always contains the current value (so a URL value outside the meta still shows). */
const withCurrent = (options, value, labelFor) => {
  if (!value || options.some((o) => o.value === value)) return options;
  return [...options, { value, label: labelFor ? labelFor(value) : value }];
};

// ---------------------------------------------------------------------------
// Small presentational bits
// ---------------------------------------------------------------------------

const countBadgeClass = (active) =>
  active ? "bg-white/20 text-white" : `${surfaces.tertiary} ${textColors.secondary}`;

const BucketTabs = ({ bucket, counts, onChange, loading }) => (
  <div role="group" aria-label="Message views" className="flex flex-wrap gap-2">
    {BUCKETS.map((value) => {
      const meta = getBucketMeta(value);
      const active = bucket === value;
      const count = counts ? counts[value] : null;
      return (
        <button
          key={value}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(value)}
          title={meta.description}
          className={`${typography.button} inline-flex items-center gap-2 px-4 py-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${
            active ? "bg-blue-600 dark:bg-blue-500 text-white shadow-sm" : `${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`
          }`}
        >
          {meta.label}
          <span
            className={`inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-xs font-medium tabular-nums ${countBadgeClass(active)}`}
            aria-label={count == null ? "count loading" : `${formatCount(count)} messages`}
          >
            {count == null ? (loading ? "…" : "–") : formatCount(count)}
          </span>
        </button>
      );
    })}
  </div>
);

BucketTabs.propTypes = {
  bucket: PropTypes.string.isRequired,
  counts: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const SkeletonRows = () => (
  <div className="space-y-3" aria-busy="true" aria-live="polite">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}>
        <div className="animate-pulse space-y-3">
          <div className={`h-4 ${surfaces.tertiary} rounded w-1/3`} />
          <div className={`h-3 ${surfaces.tertiary} rounded w-2/3`} />
          <div className={`h-3 ${surfaces.tertiary} rounded w-1/2`} />
        </div>
      </div>
    ))}
    <span className="sr-only">Loading messages…</span>
  </div>
);

const Dash = () => <span className={textColors.muted}>—</span>;

/** When column: scheduled_for / sent_at / most relevant history timestamp, relative + absolute. */
const WhenCell = ({ row, bucket }) => {
  let label = "";
  let value = null;
  let hint = "";
  if (bucket === "scheduled") {
    label = row.status === "sending" ? "Sending" : "Scheduled for";
    value = row.scheduled_for;
    hint = row.status === "sending" ? "being delivered now" : "no earlier than";
  } else if (bucket === "sent") {
    label = "Sent";
    value = row.sent_at || row.updated_at;
  } else {
    const ts = historyTimestamp(row);
    label = ts.label;
    value = ts.value;
  }
  return (
    <div className="min-w-0" title={value || ""}>
      <div className={`${typography.caption} ${textColors.muted} uppercase tracking-wider`}>{label}</div>
      {value ? (
        <>
          <div className={`${typography.bodySmall} ${textColors.primary} font-medium whitespace-nowrap`}>{formatRelativeTime(value)}</div>
          <div className={`${typography.caption} ${textColors.tertiary} whitespace-nowrap`}>{formatDateTime(value)}</div>
          {hint ? <div className={`${typography.caption} ${textColors.muted} italic`}>{hint}</div> : null}
        </>
      ) : (
        <Dash />
      )}
    </div>
  );
};

WhenCell.propTypes = { row: PropTypes.object.isRequired, bucket: PropTypes.string.isRequired };

const RecipientCell = ({ row }) => {
  const recipient = describeRecipient(row);
  return (
    <div className="min-w-0 space-y-1">
      {recipient.name ? (
        <div className={`${typography.bodySmall} ${textColors.primary} font-medium break-words`}>{recipient.name}</div>
      ) : null}
      {recipient.email ? (
        <div className={`${typography.caption} ${textColors.tertiary} break-all`} title={recipient.email}>
          {recipient.email}
        </div>
      ) : null}
      {!recipient.name && !recipient.email ? <Dash /> : null}
      <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
        <RecipientKindBadge kind={row.recipient_kind} />
        {row.recipient_kind === "user" ? <PaidChip value={row.recipient_was_paid} /> : null}
      </div>
    </div>
  );
};

RecipientCell.propTypes = { row: PropTypes.object.isRequired };

const TitleCell = ({ row }) => (
  <div className="min-w-0">
    <div className={`${typography.bodySmall} ${textColors.primary} font-medium break-words line-clamp-2`} title={row.title || ""}>
      {row.title || <span className={textColors.muted}>(untitled)</span>}
    </div>
    {row.body_preview ? (
      <div className={`${typography.caption} ${textColors.tertiary} break-words line-clamp-2`} title={row.body_preview}>
        {row.body_preview}
      </div>
    ) : null}
    {row.sender_display_name ? (
      <div className={`${typography.caption} ${textColors.muted} truncate`} title={`Sender: ${row.sender_display_name}`}>
        From {row.sender_display_name}
      </div>
    ) : null}
  </div>
);

TitleCell.propTypes = { row: PropTypes.object.isRequired };

const StatusCell = ({ row }) => {
  const attempts = Number(row.attempts || 0);
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <MessageStatusBadge status={row.status} />
        {row.error ? (
          <span
            className="inline-flex text-red-600 dark:text-red-400 cursor-help"
            title={row.error}
            role="img"
            aria-label={`Error: ${row.error}`}
          >
            <ErrorIcon size="sm" aria-hidden="true" />
          </span>
        ) : null}
      </div>
      <div className={`${typography.caption} ${textColors.tertiary} tabular-nums`}>
        {attempts === 0 ? "No attempts yet" : `${formatCount(attempts)} attempt${attempts === 1 ? "" : "s"}`}
      </div>
    </div>
  );
};

StatusCell.propTypes = { row: PropTypes.object.isRequired };

const Pagination = ({ page, pageSize, total, count, hasMore, fetching, onPage }) => {
  const first = total === 0 || count === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = first === 0 ? 0 : first + count - 1;
  const pages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p className={`${typography.caption} ${textColors.tertiary}`} aria-live="polite">
        Showing {formatCount(first)}–{formatCount(last)} of {formatCount(total)}
        {fetching ? " · updating…" : ""}
      </p>
      <div className="flex items-center gap-2">
        <SecondaryButton size="sm" onClick={() => onPage(page - 1)} disabled={page <= 1 || fetching}>
          Previous
        </SecondaryButton>
        <span className={`${typography.caption} ${textColors.secondary} tabular-nums whitespace-nowrap`}>
          Page {formatCount(page)} of {formatCount(pages)}
        </span>
        <SecondaryButton size="sm" onClick={() => onPage(page + 1)} disabled={!hasMore || fetching}>
          Next
        </SecondaryButton>
      </div>
    </div>
  );
};

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  hasMore: PropTypes.bool,
  fetching: PropTypes.bool,
  onPage: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const MessagesAdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFiltersFromSearchParams(searchParams), [searchParams]);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const bucketMeta = getBucketMeta(filters.bucket);
  const activeFilterCount = countActiveFilters(filters);

  const queryClient = useQueryClient();
  const apiParams = useMemo(() => filtersToApiParams(filters), [filters]);
  const listQuery = useAdminMessages(apiParams);
  const metaQuery = useAdminMessagesFiltersMeta();
  const meta = metaQuery.meta;
  const cancelMutation = useCancelAdminMessage();
  const { toast, show: showToast, dismiss: dismissToast } = useToast();

  // URL is the single source of truth for the filters.
  const applyFilters = useCallback(
    (next, { replace = true } = {}) => {
      setSearchParams(filtersToSearchParams(next), { replace });
    },
    [setSearchParams]
  );
  const updateFilters = useCallback(
    (patch, { resetPage = true, replace = true } = {}) => {
      const current = filtersRef.current;
      applyFilters(normalizeFilters({ ...current, ...patch, page: resetPage ? 1 : (patch.page ?? current.page) }), { replace });
    },
    [applyFilters]
  );

  // Debounced search (the URL gets the value 350 ms after the last keystroke).
  const [searchText, setSearchText] = useState(filters.q);
  const searchTimer = useRef(null);
  const lastAppliedQ = useRef(filters.q);
  useEffect(() => {
    if (filters.q !== lastAppliedQ.current) {
      lastAppliedQ.current = filters.q;
      setSearchText(filters.q);
    }
  }, [filters.q]);
  useEffect(() => () => searchTimer.current && clearTimeout(searchTimer.current), []);
  const onSearchChange = (value, { immediate = false } = {}) => {
    setSearchText(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const commit = () => {
      const q = value.trim();
      lastAppliedQ.current = q;
      updateFilters({ q });
    };
    if (immediate) commit();
    else searchTimer.current = setTimeout(commit, SEARCH_DEBOUNCE_MS);
  };

  // Derived data
  const results = useMemo(() => (Array.isArray(listQuery.data?.results) ? listQuery.data.results : []), [listQuery.data]);
  const total = Number(listQuery.data?.total || 0);
  const hasMore = Boolean(listQuery.data?.has_more);
  const counts = useMemo(() => (listQuery.data?.counts ? bucketCounts(listQuery.data.counts) : null), [listQuery.data]);
  const statusCounts = listQuery.data?.counts || {};

  const automationOptions = useMemo(
    () =>
      withCurrent(
        [{ value: "", label: "All automations" }, ...(meta.automations || []).map((a) => ({ value: String(a.id), label: a.name }))],
        filters.automation_id,
        (v) => `Automation #${v}`
      ),
    [meta.automations, filters.automation_id]
  );
  const systemKindOptions = useMemo(
    () =>
      withCurrent(
        [{ value: "", label: "All system kinds" }, ...(meta.origin_details || []).map((d) => ({ value: d, label: formatOriginDetail(d) }))],
        filters.origin_detail,
        formatOriginDetail
      ),
    [meta.origin_details, filters.origin_detail]
  );
  const templateOptions = useMemo(
    () =>
      withCurrent(
        [{ value: "", label: "All templates" }, ...(meta.email_template_variants || []).map((v) => ({ value: v, label: formatEmailVariant(v) }))],
        filters.email_template_variant,
        formatEmailVariant
      ),
    [meta.email_template_variants, filters.email_template_variant]
  );
  const orderingOptions = useMemo(
    () => [{ value: "", label: defaultOrderingLabel(filters.bucket) }, ...ORDERING_OPTIONS],
    [filters.bucket]
  );

  const paidEnabled = filters.recipient_kind === "" || filters.recipient_kind === "user";

  // Detail modal + cancel flow
  const [detailId, setDetailId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const selectAutomation = (automation) => {
    if (!automation?.id) return;
    setDetailId(null);
    updateFilters({ origin: "automation", automation_id: String(automation.id) });
  };

  const handleCancelConfirm = async () => {
    const target = cancelTarget;
    if (!target) return;
    try {
      await cancelMutation.mutateAsync(target.id);
      showToast("success", `Cancelled “${target.title || "message"}” — it will not be sent.`);
    } catch (err) {
      if (err?.status === 409) {
        showToast("error", `${err.message || "Only scheduled messages can be cancelled."} The list has been refreshed.`);
        queryClient.invalidateQueries({ queryKey: ADMIN_MESSAGES_QUERY_KEY });
      } else {
        showToast("error", err?.message || "Failed to cancel the message.");
      }
    }
  };

  const refreshAll = () => {
    listQuery.refetch();
    metaQuery.refetch();
  };

  const isEmpty = !listQuery.isLoading && !listQuery.error && total === 0;
  const pageOutOfRange = !listQuery.isLoading && !listQuery.error && total > 0 && results.length === 0;
  const dateLabel = bucketMeta.dateFilterLabel;
  const dateFromKey = filters.bucket === "scheduled" ? "scheduled_from" : "created_from";
  const dateToKey = filters.bucket === "scheduled" ? "scheduled_to" : "created_to";

  const renderRowActions = (row, { compact = false } = {}) => (
    <div className={`flex items-center ${compact ? "flex-wrap" : "justify-end"} gap-2`}>
      <GhostButton size="sm" leftIcon={<EyeIcon size="sm" />} onClick={() => setDetailId(row.id)} aria-label={`View message #${row.id}`}>
        View
      </GhostButton>
      {isCancellable(row) ? (
        <GhostButton
          size="sm"
          variant="danger"
          leftIcon={<XIcon size="sm" />}
          onClick={() => setCancelTarget(row)}
          disabled={cancelMutation.isPending && cancelMutation.variables === row.id}
          title="Cancel this scheduled message"
          aria-label={`Cancel message #${row.id}`}
        >
          Cancel
        </GhostButton>
      ) : null}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className={`${typography.h2} ${textColors.primary}`}>Messages</h1>
          <p className={`mt-1 ${typography.bodySmall} ${textColors.secondary} max-w-3xl`}>
            Every message and email the platform sent or has scheduled — automations, broadcasts and system emails — with the
            recipient, template and delivery status of each one.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 shrink-0">
          <SecondaryButton onClick={refreshAll} disabled={listQuery.isFetching} className="w-full sm:w-auto whitespace-nowrap">
            {listQuery.isFetching ? "Refreshing…" : "Refresh"}
          </SecondaryButton>
        </div>
      </div>

      {/* Bucket tabs */}
      <BucketTabs
        bucket={filters.bucket}
        counts={counts}
        loading={listQuery.isLoading}
        onChange={(bucket) => {
          if (bucket === filters.bucket) return;
          applyFilters(filtersForBucket(filters, bucket), { replace: false });
        }}
      />

      {/* Filters */}
      <Card variant="glass">
        <CardHeader
          title="Filters"
          subtitle={activeFilterCount > 0 ? `${formatCount(activeFilterCount)} active · the view is shareable via its URL` : "No filters applied · the view is shareable via its URL"}
          actions={
            <GhostButton
              size="sm"
              onClick={() => {
                if (searchTimer.current) clearTimeout(searchTimer.current);
                applyFilters(clearedFilters(filters));
              }}
              disabled={activeFilterCount === 0 && !filters.ordering}
            >
              Clear filters
            </GhostButton>
          }
        />
        <CardBody className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <FormField label="Search" htmlFor="messages-search">
                <SearchInput
                  id="messages-search"
                  value={searchText}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onClear={() => onSearchChange("", { immediate: true })}
                  placeholder="Search recipient, email or title"
                  aria-label="Search recipient, email or title"
                />
              </FormField>
            </div>
            <FormField label="Recipient kind" htmlFor="messages-kind">
              <Select
                id="messages-kind"
                value={filters.recipient_kind}
                onChange={(e) => updateFilters({ recipient_kind: e.target.value })}
                options={KIND_OPTIONS}
                placeholder={null}
              />
            </FormField>
            <FormField label="Paid at send" htmlFor="messages-paid" helperText="Snapshot at send time — users only">
              <Select
                id="messages-paid"
                value={filters.recipient_was_paid}
                onChange={(e) => updateFilters({ recipient_was_paid: e.target.value })}
                options={PAID_OPTIONS}
                placeholder={null}
                disabled={!paidEnabled}
                title={paidEnabled ? undefined : "Only applies to users"}
              />
            </FormField>

            <FormField label="Origin" htmlFor="messages-origin">
              <Select
                id="messages-origin"
                value={filters.origin}
                onChange={(e) => updateFilters({ origin: e.target.value, automation_id: "", origin_detail: "" })}
                options={ORIGIN_OPTIONS}
                placeholder={null}
              />
            </FormField>
            {filters.origin === "automation" ? (
              <FormField label="Automation" htmlFor="messages-automation">
                <Select
                  id="messages-automation"
                  value={filters.automation_id}
                  onChange={(e) => updateFilters({ automation_id: e.target.value })}
                  options={automationOptions}
                  placeholder={null}
                  disabled={metaQuery.isLoading}
                />
              </FormField>
            ) : filters.origin === "system" ? (
              <FormField label="System kind" htmlFor="messages-system-kind">
                <Select
                  id="messages-system-kind"
                  value={filters.origin_detail}
                  onChange={(e) => updateFilters({ origin_detail: e.target.value })}
                  options={systemKindOptions}
                  placeholder={null}
                  disabled={metaQuery.isLoading}
                />
              </FormField>
            ) : null}
            <FormField label="Channel" htmlFor="messages-channel">
              <Select
                id="messages-channel"
                value={filters.channel}
                onChange={(e) => updateFilters({ channel: e.target.value })}
                options={CHANNEL_OPTIONS}
                placeholder={null}
              />
            </FormField>
            <FormField label="Email template" htmlFor="messages-template">
              <Select
                id="messages-template"
                value={filters.email_template_variant}
                onChange={(e) => updateFilters({ email_template_variant: e.target.value })}
                options={templateOptions}
                placeholder={null}
                disabled={metaQuery.isLoading}
              />
            </FormField>

            <FormField label={`${dateLabel} from`} htmlFor="messages-date-from">
              <Input
                id="messages-date-from"
                type="date"
                value={filters[dateFromKey]}
                onChange={(e) => updateFilters({ [dateFromKey]: e.target.value })}
                max={filters[dateToKey] || undefined}
              />
            </FormField>
            <FormField label={`${dateLabel} to`} htmlFor="messages-date-to">
              <Input
                id="messages-date-to"
                type="date"
                value={filters[dateToKey]}
                onChange={(e) => updateFilters({ [dateToKey]: e.target.value })}
                min={filters[dateFromKey] || undefined}
              />
            </FormField>
            <FormField label="Ordering" htmlFor="messages-ordering">
              <Select
                id="messages-ordering"
                value={filters.ordering}
                onChange={(e) => updateFilters({ ordering: e.target.value })}
                options={orderingOptions}
                placeholder={null}
              />
            </FormField>
            <FormField label="Page size" htmlFor="messages-page-size">
              <Select
                id="messages-page-size"
                value={String(filters.page_size)}
                onChange={(e) => updateFilters({ page_size: Number(e.target.value) })}
                options={PAGE_SIZE_SELECT_OPTIONS}
                placeholder={null}
              />
            </FormField>
          </div>

          {filters.bucket === "history" ? (
            <div className={`pt-3 border-t ${borders.glass}`}>
              <div className={`${typography.label} ${textColors.secondary} mb-2`} id="messages-status-label">
                Status
                <span className={`ml-2 ${typography.caption} ${textColors.muted} font-normal`}>
                  {filters.status.length === 0 ? "all history statuses" : `${filters.status.length} selected`}
                </span>
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-labelledby="messages-status-label">
                {bucketMeta.statuses.map((status) => {
                  const selected = filters.status.includes(status);
                  const count = statusCounts[status];
                  return (
                    <button
                      key={status}
                      type="button"
                      aria-pressed={selected}
                      onClick={() =>
                        updateFilters({
                          status: selected ? filters.status.filter((s) => s !== status) : [...filters.status, status],
                        })
                      }
                      title={getMessageStatusMeta(status).description}
                      className={`${typography.button} inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${
                        selected ? "bg-blue-600 dark:bg-blue-500 text-white" : `${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`
                      }`}
                    >
                      {getMessageStatusMeta(status).label}
                      <span className={`tabular-nums ${selected ? "text-white/80" : textColors.tertiary}`}>
                        {count == null ? "–" : formatCount(count)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>

      {/* Meta warning */}
      {metaQuery.error ? (
        <div className="rounded-2xl px-4 py-3 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800" role="alert">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className={`${typography.bodySmall} text-yellow-800 dark:text-yellow-300`}>
              Filter choices could not be loaded ({metaQuery.error?.message || "Unknown error"}) — the automation, system kind
              and template lists only show the values already in the URL.
            </p>
            <SecondaryButton size="sm" onClick={() => metaQuery.refetch()} disabled={metaQuery.isFetching}>
              Retry
            </SecondaryButton>
          </div>
        </div>
      ) : null}

      {/* Results */}
      <Card variant="glass">
        <CardHeader
          title={bucketMeta.title}
          subtitle={
            listQuery.isLoading
              ? "Loading…"
              : listQuery.error
                ? "Could not load messages."
                : `${bucketMeta.description} ${total > 0 ? `· ${formatCount(total)} message${total === 1 ? "" : "s"}` : ""}`
          }
        />
        <CardBody className="mt-4 space-y-4">
          {listQuery.error ? (
            <div className="rounded-2xl px-4 py-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800" role="alert">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>
                  Failed to load messages: {listQuery.error?.message || "Unknown error"}
                </p>
                <div className="flex items-center gap-2">
                  {activeFilterCount > 0 ? (
                    <SecondaryButton size="sm" onClick={() => applyFilters(clearedFilters(filters))}>
                      Clear filters
                    </SecondaryButton>
                  ) : null}
                  <SecondaryButton size="sm" onClick={() => listQuery.refetch()} disabled={listQuery.isFetching}>
                    Retry
                  </SecondaryButton>
                </div>
              </div>
            </div>
          ) : listQuery.isLoading ? (
            <SkeletonRows />
          ) : isEmpty ? (
            <div className={`rounded-2xl ${surfaces.secondary}`}>
              {activeFilterCount > 0 ? (
                <EmptyState
                  icon={<ClockIcon size="xl" className={textColors.muted} />}
                  title="No messages match"
                  description="Try another search term or clear the filters."
                  action={<SecondaryButton onClick={() => applyFilters(clearedFilters(filters))}>Clear filters</SecondaryButton>}
                />
              ) : (
                <EmptyState
                  icon={<ClockIcon size="xl" className={textColors.muted} />}
                  title={bucketMeta.emptyTitle}
                  description={bucketMeta.emptyDescription}
                />
              )}
            </div>
          ) : pageOutOfRange ? (
            <div className={`rounded-2xl ${surfaces.secondary}`}>
              <EmptyState
                title="No results on this page"
                description={`This view has ${formatCount(total)} message${total === 1 ? "" : "s"}, but page ${formatCount(filters.page)} is past the end.`}
                action={<SecondaryButton onClick={() => updateFilters({ page: 1 }, { resetPage: false })}>Go to the first page</SecondaryButton>}
              />
            </div>
          ) : (
            <div className={listQuery.isFetching ? "opacity-70 transition-opacity" : "transition-opacity"} aria-busy={listQuery.isFetching}>
              {/* Mobile + tablet: cards */}
              <ol className="space-y-3 lg:hidden" aria-label={bucketMeta.title}>
                {results.map((row) => (
                  <li key={row.id} className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <TitleCell row={row} />
                      <MessageStatusBadge status={row.status} />
                    </div>
                    <dl className={`mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t ${borders.glass}`}>
                      <div>
                        <dt className="sr-only">{bucketMeta.whenLabel}</dt>
                        <dd>
                          <WhenCell row={row} bucket={filters.bucket} />
                        </dd>
                      </div>
                      <div>
                        <dt className={`${typography.caption} ${textColors.muted} uppercase tracking-wider`}>Recipient</dt>
                        <dd className="mt-0.5">
                          <RecipientCell row={row} />
                        </dd>
                      </div>
                      <div>
                        <dt className={`${typography.caption} ${textColors.muted} uppercase tracking-wider`}>Channel · template</dt>
                        <dd className="mt-1 flex flex-wrap items-center gap-1.5">
                          <ChannelChip channel={row.channel} />
                          <TemplateChip channel={row.channel} variant={row.email_template_variant} />
                        </dd>
                      </div>
                      <div>
                        <dt className={`${typography.caption} ${textColors.muted} uppercase tracking-wider`}>Origin</dt>
                        <dd className="mt-0.5">
                          <OriginLabel message={row} onAutomationSelect={selectAutomation} />
                        </dd>
                      </div>
                      <div>
                        <dt className={`${typography.caption} ${textColors.muted} uppercase tracking-wider`}>Delivery</dt>
                        <dd className="mt-1">
                          <StatusCell row={row} />
                        </dd>
                      </div>
                    </dl>
                    <div className={`mt-3 pt-3 border-t ${borders.glass}`}>{renderRowActions(row, { compact: true })}</div>
                  </li>
                ))}
              </ol>

              {/* Desktop: table */}
              <div className={`hidden lg:block rounded-2xl border ${borders.glass} overflow-hidden`}>
                <div className={`overflow-x-auto ${surfaces.glassCard}`}>
                  <table className="min-w-full text-left" aria-label={bucketMeta.title}>
                    <thead className={surfaces.glass}>
                      <tr className={`${typography.caption} ${textColors.secondary}`}>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">{bucketMeta.whenLabel}</th>
                        <th className="px-3 py-2 font-medium">Recipient</th>
                        <th className="px-3 py-2 font-medium">Channel</th>
                        <th className="px-3 py-2 font-medium">Title</th>
                        <th className="px-3 py-2 font-medium">Origin</th>
                        <th className="px-3 py-2 font-medium">Template</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium text-right">
                          <span className="sr-only">Row actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((row) => (
                        <tr key={row.id} className={`border-t ${borders.glass} align-top ${surfaces.hover}`}>
                          <td className="px-3 py-3 min-w-[150px]">
                            <WhenCell row={row} bucket={filters.bucket} />
                          </td>
                          <td className="px-3 py-3 min-w-[200px] max-w-[260px]">
                            <RecipientCell row={row} />
                          </td>
                          <td className="px-3 py-3">
                            <ChannelChip channel={row.channel} />
                          </td>
                          <td className="px-3 py-3 min-w-[220px] max-w-[320px]">
                            <TitleCell row={row} />
                          </td>
                          <td className="px-3 py-3 min-w-[160px] max-w-[220px]">
                            <OriginLabel message={row} onAutomationSelect={selectAutomation} />
                          </td>
                          <td className="px-3 py-3">
                            <TemplateChip channel={row.channel} variant={row.email_template_variant} />
                          </td>
                          <td className="px-3 py-3 min-w-[140px]">
                            <StatusCell row={row} />
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">{renderRowActions(row)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4">
                <Pagination
                  page={filters.page}
                  pageSize={filters.page_size}
                  total={total}
                  count={results.length}
                  hasMore={hasMore}
                  fetching={listQuery.isFetching}
                  onPage={(page) => updateFilters({ page: Math.max(1, page) }, { resetPage: false })}
                />
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Suppression list */}
      <EmailSuppressionCard showToast={showToast} />

      {/* Detail */}
      <MessageDetailModal
        isOpen={detailId != null}
        messageId={detailId}
        onClose={() => setDetailId(null)}
        onCancel={(message) => setCancelTarget(message)}
        cancelPending={cancelMutation.isPending && cancelMutation.variables === detailId}
        onAutomationSelect={selectAutomation}
      />

      {/* Cancel confirmation */}
      <ConfirmDialog
        isOpen={!!cancelTarget}
        onClose={() => {
          if (cancelMutation.isPending) return;
          setCancelTarget(null);
        }}
        onConfirm={handleCancelConfirm}
        variant="warning"
        title="Cancel this scheduled message?"
        message={`“${cancelTarget?.title || "This message"}” to ${cancelTarget?.recipient_email || cancelTarget?.user?.email || "the recipient"} will not be sent${
          cancelTarget?.scheduled_for ? ` (it was scheduled for ${formatDateTime(cancelTarget.scheduled_for)})` : ""
        }. This cannot be undone.`}
        confirmText="Cancel message"
        cancelText="Keep it"
        isLoading={cancelMutation.isPending}
        size="md"
      />
    </div>
  );
};

export default MessagesAdminPage;
